"use client";

import React from "react";
import { Calendar, User, ArrowRight, Clock } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";



function MainPostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-3xl animate-pulse space-y-8 py-12">
        <div className="h-12 w-3/4 rounded-lg bg-muted/50"></div>
        <div className="flex gap-4">
          <div className="h-6 w-24 rounded bg-muted/50"></div>
          <div className="h-6 w-24 rounded bg-muted/50"></div>
        </div>
        <div className="aspect-video w-full rounded-2xl bg-muted/50"></div>
        <div className="space-y-4">
          <div className="h-4 w-full rounded bg-muted/50"></div>
          <div className="h-4 w-full rounded bg-muted/50"></div>
          <div className="h-4 w-5/6 rounded bg-muted/50"></div>
        </div>
      </div>
    );
  }

  if (error || posts.length === 0) {
    return (
      <div className="mx-auto max-w-3xl py-24 text-center">
        <h2 className="font-default text-2xl font-semibold text-muted-foreground">
          Article not found.
        </h2>
        <p className="mt-4 text-muted-foreground">
          The wellness insight you are looking for is currently unavailable.
        </p>
        <Link to="/HomePage">
          <Button variant="outline" className="font-default border-primary text-primary hover:bg-primary hover:text-primary-foreground">Return Home</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];

  return (
    <article className="mx-auto max-w-3xl">
      {}
      <header className="mb-10 text-center md:mb-16">
        {post.categories && post.categories.length > 0 && (
          <div className="mb-6 flex flex-wrap justify-center gap-2">
            {post.categories.map((cat, i) => (
              <span
                key={cat.id}
                data-index={i}
                className="rounded-full bg-primary/5 px-4 py-1 text-xs font-medium tracking-wider uppercase text-primary"
              >
                {cat.name}
              </span>
            ))}
          </div>
        )}
        <h1 className="font-default text-4xl font-bold leading-tight tracking-tight text-primary md:text-5xl lg:text-6xl">
          {post.title}
        </h1>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground">
          {post.author && (
            <div className="flex items-center gap-2">
              <Avatar className="size-8 border border-border">
                <AvatarImage src={post.author.avatar?.["48"]} alt={post.author.name} />
                <AvatarFallback className="font-default">{post.author.name.charAt(0)}</AvatarFallback>
              </Avatar>
              <span className="font-default font-medium text-foreground">{post.author.name}</span>
            </div>
          )}
          {post.date && (
            <div className="flex items-center gap-1.5">
              <Calendar className="size-4" />
              <time dateTime={post.date} className="font-default">{post.date}</time>
            </div>
          )}
          <div className="flex items-center gap-1.5">
            <Clock className="size-4" />
            <span className="font-default">4 min read</span>
          </div>
        </div>
      </header>

      {}
      {post.featuredImage && (
        <figure className="mb-12 overflow-hidden rounded-lg border border-border/30 bg-muted/10 shadow-sm">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="aspect-video w-full object-cover"
          />
        </figure>
      )}

      {}
      <div className="prose prose-lg max-w-none font-default leading-relaxed text-foreground/80 prose-headings:font-default prose-headings:text-primary prose-a:text-primary hover:prose-a:opacity-80 prose-img:rounded-lg">
        {post.content ? (
          <div dangerouslySetInnerHTML={{ __html: post.content }} />
        ) : (
          <p>
            {post.excerpt ||
              "Our clinical team is currently finalizing the details of this wellness protocol. Please check back soon for the full guide."}
          </p>
        )}
      </div>

      {}
      {post.tags && post.tags.length > 0 && (
        <div className="mt-12 flex flex-wrap gap-2">
          {post.tags.map((tag, i) => (
            <span
              key={tag.id}
              data-index={i}
              className="rounded-md bg-secondary px-3 py-1 text-xs font-medium text-primary/70"
            >
              #{tag.name}
            </span>
          ))}
        </div>
      )}

      <Separator className="my-12 bg-border/40" />

      {}
      {post.author && (
        <div className="flex flex-col items-center gap-6 rounded-lg border border-border/50 bg-card/30 p-8 text-center sm:flex-row sm:text-left shadow-sm">
          <Avatar className="size-24 border-2 border-primary/5">
            <AvatarImage src={post.author.avatar?.["96"]} alt={post.author.name} />
            <AvatarFallback className="font-default text-2xl">{post.author.name.charAt(0)}</AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <h3 className="font-default text-xl font-semibold text-primary">
              Insights by {post.author.name}
            </h3>
            <p className="mt-2 font-default text-sm text-muted-foreground leading-relaxed">
              A dedicated specialist at M / O Protocol, focused on delivering evidence-based wellness 
              strategies and medical insights to optimize your health journey in West Texas.
            </p>
            {post.author.link && (
              <Link to={post.author.link} className="mt-4 inline-block">
                <Button variant="link" className="h-auto p-0 text-primary font-default font-semibold">
                  View all insights <ArrowRight className="ml-1 size-4" />
                </Button>
              </Link>
            )}
          </div>
        </div>
      )}
    </article>
  );
}

function RelatedPostsContent() {
  const { posts, loading } = usePosts();

  if (loading || posts.length === 0) {
    return null;
  }

  return (
    <div className="mx-auto mt-24 max-w-5xl">
      <div className="mb-10 flex items-center justify-between">
        <h2 className="font-default text-3xl font-bold text-primary">Further Reading</h2>
        <Link to="/ServicesPage">
          <Button variant="ghost" className="hidden sm:flex font-default text-primary hover:bg-secondary">
            Explore Services <ArrowRight className="ml-2 size-4" />
          </Button>
        </Link>
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {posts.map((post, i) => (
          <Card
            key={post.id}
            data-index={i}
            className="group flex h-full flex-col overflow-hidden border-border/40 bg-card/50 transition-all duration-300 hover:shadow-md hover:border-border"
          >
            {post.featuredImage && (
              <div className="aspect-[16/10] w-full overflow-hidden">
                <img
                  src={post.featuredImage}
                  alt={post.title}
                  className="size-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
            )}
            <CardHeader className="flex-1 pb-4">
              {post.categories && post.categories.length > 0 && (
                <span className="mb-2 text-xs font-medium tracking-widest uppercase text-primary/60">
                  {post.categories[0].name}
                </span>
              )}
              <CardTitle className="font-default text-xl leading-tight text-primary line-clamp-2 group-hover:text-primary/80 transition-colors">
                {post.title}
              </CardTitle>
            </CardHeader>
            <CardContent className="pb-6">
              <p className="font-default text-sm text-muted-foreground line-clamp-3 leading-relaxed">
                {post.excerpt?.replace(/<[^>]*>?/gm, "") || "Explore our latest medical insights and wellness protocols designed for your longevity..."}
              </p>
            </CardContent>
            <CardFooter className="pt-0">
              <Link to={post.link || "#"} className="w-full">
                <Button variant="outline" className="w-full border-border/60 bg-background/50 font-default text-primary hover:bg-primary hover:text-primary-foreground transition-colors">
                  Read Article
                </Button>
              </Link>
            </CardFooter>
          </Card>
        ))}
      </div>

      <div className="mt-8 flex justify-center sm:hidden">
        <Link to="/ServicesPage">
          <Button variant="outline" className="font-default border-primary text-primary">
            View All Insights <ArrowRight className="ml-2 size-4" />
          </Button>
        </Link>
      </div>
    </div>
  );
}



export default function SinglePostTemplateSection() {
  
  const currentParams =
    typeof window !== "undefined" && (window as any).wvcClient
      ? (window as any).wvcClient.getCurrentWPQueryParams()
      : { post_type: "post", per_page: 1 };

  const mainPostQuery = new WP_Query(currentParams);

  
  const relatedPostsQuery = new WP_Query({
    post_type: "post",
    per_page: 3,
    orderby: "date",
    order: "desc",
  });

  return (
    <section data-section-id="233899"
      id="single-post-template-section"
      className="bg-background text-foreground min-h-screen px-4 py-16 md:px-8 lg:py-24"
    >
      <div className="container mx-auto">
        {}
        <PostsProvider wp_query={mainPostQuery}>
          <MainPostContent />
        </PostsProvider>

        {}
        <PostsProvider wp_query={{ post_type: "post", per_page: 3, orderby: "date", order: "desc" }}>
          <RelatedPostsContent />
        </PostsProvider>
      </div>
    </section>
  );
}